import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Download, RefreshCw, Upload } from 'lucide-react'
import {
  Callout, ConfirmDialog, DataTable, DateValue, FormDialog, ImportDialog, PageHeader,
  PermissionGate,
} from '@/components/common'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { formProblem } from '@/lib/api/fieldErrors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { useDepartments } from '@/features/org'
import {
  useAccrualSettings, useChangeAccrualStart, useOrgLeaveBalances, useRunAccrual,
} from '../api'
import type { AccrualRunResult } from '../api'
import type { OrgBalanceRow } from '../api'

const OPENING_IMPORT = '/v1/leave/opening-balances'

/**
 * Everybody's balances: the screen HR opens in November.
 *
 * <p>Also where a company moving from a spreadsheet loads what each person had left. Nothing
 * else opens a balance, and without one nobody can apply for leave, so the import sits on the
 * screen that shows the gap it fills — every "Not opened" row below.
 */
export function LeaveBalancesPage() {
  useBreadcrumb()
  const [year, setYear] = useState(new Date().getFullYear())
  const [departmentId, setDepartmentId] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(50)
  const [search, setSearch] = useState('')
  const [importing, setImporting] = useState(false)
  const runAccrual = useRunAccrual()
  const accrualSettings = useAccrualSettings()
  const changeStart = useChangeAccrualStart()
  const [confirmingRun, setConfirmingRun] = useState(false)
  const [editingStart, setEditingStart] = useState<string | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [accrualResult, setAccrualResult] = useState<AccrualRunResult | null>(null)
  const [accrualError, setAccrualError] = useState<string | null>(null)

  const balances = useOrgLeaveBalances({ year, departmentId, q: search, page, size })
  const departments = useDepartments()

  const columns = useMemo<ColumnDef<OrgBalanceRow, unknown>[]>(() => [
    { id: 'employeeCode', header: 'Code', accessorFn: (row) => row.employeeCode },
    { id: 'employeeName', header: 'Name', accessorFn: (row) => row.employeeName },
    {
      id: 'leaveTypeName',
      header: 'Leave type',
      accessorFn: (row) => row.leaveTypeName ?? '—',
      cell: ({ row }) => row.original.leaveTypeName
        ?? <span className="text-slate-500" title={row.original.note ?? ''}>None opened</span>,
    },
    {
      id: 'entitled',
      header: 'Entitled',
      accessorFn: (row) => row.entitled,
      cell: ({ row }) => row.original.entitled ?? <span className="text-slate-500">—</span>,
    },
    {
      // Kept apart from what this system accrued, so a figure that came from the old system
      // can be told from one HUMIS worked out — which is the first thing an auditor asks.
      id: 'broughtForward',
      header: 'Brought forward',
      accessorFn: (row) => row.broughtForward,
      cell: ({ row }) => row.original.broughtForward == null
        ? <span className="text-slate-500">—</span>
        : (
          <span>
            {row.original.broughtForward}
            <span className="block text-xs text-slate-500">
              as of {row.original.broughtForwardAsOf}
            </span>
          </span>
        ),
    },
    {
      id: 'accruedSince',
      header: 'Accrued by HUMIS',
      accessorFn: (row) => row.accruedSince,
      cell: ({ row }) => row.original.accruedSince ?? <span className="text-slate-500">—</span>,
    },
    {
      id: 'used',
      header: 'Taken',
      accessorFn: (row) => row.used,
      cell: ({ row }) => row.original.used ?? <span className="text-slate-500">—</span>,
    },
    {
      id: 'pending',
      header: 'Requested',
      accessorFn: (row) => row.pending,
      cell: ({ row }) => row.original.pending ?? <span className="text-slate-500">—</span>,
    },
    {
      id: 'available',
      header: 'Left',
      accessorFn: (row) => row.available,
      cell: ({ row }) => row.original.available == null
        ? <span className="text-slate-500" title={row.original.note ?? ''}>Not opened</span>
        : <span className="font-medium">{row.original.available}</span>,
    },
  ], [])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Leave balances"
        subtitle="Everybody, for the year — including the people who have no balance at all."
        actions={
          <>
            <PermissionGate permission="leave:read-all">
              <Button
                type="button" variant="outline"
                onClick={() => {
                  void downloadFile(bulkPaths(OPENING_IMPORT).export, 'leave-balances.xlsx')
                }}
              >
                <Download className="size-4" aria-hidden="true" />
                Export
              </Button>
            </PermissionGate>
            <PermissionGate permission="leave:configure">
              <Button
                type="button" variant="outline"
                disabled={runAccrual.isPending}
                onClick={() => setConfirmingRun(true)}
              >
                <RefreshCw className="size-4" aria-hidden="true" />
                {runAccrual.isPending ? 'Running…' : 'Run accrual'}
              </Button>
            </PermissionGate>
            <PermissionGate permission="leave:configure">
              <Button type="button" variant="outline" onClick={() => setImporting(true)}>
                <Upload className="size-4" aria-hidden="true" />
                Import opening balances
              </Button>
            </PermissionGate>
          </>
        }
      />

      {accrualSettings.data && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-muted)]">
          <span>
            HUMIS accrues leave from{' '}
            <span className="font-medium text-[var(--color-heading)]">
              <DateValue value={accrualSettings.data.startsOn} />
            </span>
            {!accrualSettings.data.chosen && ' (the month this workspace was created)'}.
            Earlier months belong to your previous system — import them as opening balances.
          </span>
          <PermissionGate permission="leave:configure">
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => { setStartError(null); setEditingStart(accrualSettings.data.startsOn) }}
            >
              Edit
            </Button>
          </PermissionGate>
        </p>
      )}

      <ConfirmDialog
        open={confirmingRun}
        onOpenChange={setConfirmingRun}
        title="Run accrual?"
        description={accrualSettings.data
          ? `This credits leave from ${accrualSettings.data.creditsFrom} to ${accrualSettings.data.creditsThrough}, `
            + 'for every month not already credited. Months already credited are not credited again, '
            + 'so it is safe to run more than once.'
          : 'This credits every month of this year not already credited.'}
        confirmLabel="Run accrual"
        loading={runAccrual.isPending}
        onConfirm={() => {
          setAccrualResult(null)
          setAccrualError(null)
          runAccrual.mutate(undefined, {
            onSuccess: (result) => { setAccrualResult(result); setConfirmingRun(false) },
            onError: (caught) => {
              setAccrualError(toApiProblem(caught)?.detail ?? 'Accrual could not be run.')
              setConfirmingRun(false)
            },
          })
        }}
      />

      <FormDialog
        open={editingStart !== null}
        onOpenChange={(open) => { if (!open) setEditingStart(null) }}
        title="When does HUMIS start accruing leave?"
        description="Usually your go-live date. Nothing before it is credited by HUMIS — those months come in as opening balances. It cannot be moved past leave HUMIS has already credited."
        submitLabel="Save"
        loading={changeStart.isPending}
        error={startError}
        onSubmit={() => {
          if (!editingStart) return
          setStartError(null)
          changeStart.mutate(editingStart, {
            onSuccess: () => setEditingStart(null),
            onError: (caught) => setStartError(formProblem(caught, 'The date could not be changed.').summary),
          })
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="accrual-start">Accrues from</Label>
          <DatePicker
            id="accrual-start"
            value={editingStart ?? ''}
            onChange={(event) => setEditingStart(event.target.value)}
          />
        </div>
      </FormDialog>

      {accrualResult && (
        <p role="status" className="text-sm text-[var(--color-success)]">
          {accrualResult.entries === 0
            ? 'Everything was already up to date — nothing new to credit.'
            : `${accrualResult.daysAccrued} day(s) credited to ${accrualResult.employees} person(s)`
              + (Number(accrualResult.daysCarried) > 0 ? `, ${accrualResult.daysCarried} carried forward` : '')
              + (Number(accrualResult.daysLapsed) > 0 ? `, ${accrualResult.daysLapsed} lapsed at year end` : '')
              + '.'}
        </p>
      )}
      {accrualError && <p role="alert" className="text-sm text-[var(--color-danger)]">{accrualError}</p>}

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="opening balances"
        prefix={OPENING_IMPORT}
        invalidateKey={['leave', 'balances']}
      />

      <Callout title="Brought forward is not accrued">
        A balance loaded from your previous system shows that figure under "Brought forward",
        with the date it was true on. "Accrued by HUMIS" is only what this system has added
        since — the two are never mixed into one number.
      </Callout>

      <Callout title="“Not opened” is not zero">
        Somebody with no balance row shows "Not opened", not zero. A balance that was never
        accrued and one that has been used up are opposite situations, and a grid that renders
        both as 0 sends you to the wrong conversation.
      </Callout>

      <DataTable<OrgBalanceRow>
        columns={columns}
        data={balances.data}
        isLoading={balances.isPending}
        error={balances.error}
        onRetry={() => void balances.refetch()}
        page={page}
        size={size}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        emptyTitle="Nothing to show"
        emptyDescription="Change the year or the department."
        toolbar={
          <>
            <Input
              aria-label="Year"
              id="balance-year" type="number" className="w-28" value={String(year)}
              onChange={(event) => { setYear(Number(event.target.value)); setPage(0) }}
            />
            <Select
              aria-label="Department"
              id="balance-department"
              value={departmentId}
              onChange={(event) => { setDepartmentId(event.target.value); setPage(0) }}
            >
              <option value="">Every department</option>
              {departments.data?.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </Select>
          </>
        }
      />
    </div>
  )
}
